/**
 * Expected signals per pillar — what data is possible for each pillar,
 * and how the user can add each missing signal.
 *
 * Used by PillarCard (compact missing list) and PillarModal (full breakdown).
 */

export interface ExpectedSignal {
  label: string;
  howToAdd: string;
}

/** All possible signal labels per pillar, with guidance on how to add each. */
export const EXPECTED_SIGNALS: Record<string, ExpectedSignal[]> = {
  sleep: [
    { label: 'Sleep Score', howToAdd: 'Wear your Oura Ring to bed' },
    { label: 'Readiness', howToAdd: 'Oura Ring — auto-tracked overnight' },
    { label: 'Sleep Efficiency', howToAdd: 'Oura Ring — auto-tracked overnight' },
    { label: 'Duration', howToAdd: 'Oura Ring — auto-tracked overnight' },
    { label: 'Deep Sleep', howToAdd: 'Oura Ring — auto-tracked overnight' },
    { label: 'Circadian Phase', howToAdd: 'Wear your ring consistently (5+ nights)' },
    { label: 'Sleep Consistency', howToAdd: 'Wear your ring consistently (5+ nights)' },
  ],
  movement: [
    { label: 'Steps', howToAdd: 'Wear Apple Watch or Oura Ring during the day' },
    { label: 'Exercise', howToAdd: 'Start a workout on Apple Watch, or walk 30+ min/day' },
  ],
  nutrition: [
    { label: 'Diet Quality', howToAdd: 'Complete a check-in and rate your diet' },
    { label: 'Appetite', howToAdd: 'Complete a check-in and rate your appetite' },
    { label: 'Weight Delta', howToAdd: 'Log weight via Apple Health or smart scale' },
    { label: 'BMI', howToAdd: 'Log weight and height via Apple Health' },
    { label: 'Body Fat', howToAdd: 'Use a smart scale that syncs with Apple Health' },
  ],
  stress: [
    { label: 'Oura Stress', howToAdd: 'Wear Oura Ring during the day (firmware 2.9+)' },
    { label: 'HRV', howToAdd: 'Oura Ring — auto-tracked overnight' },
    { label: 'Resting HR', howToAdd: 'Apple Watch or Oura Ring — auto-tracked' },
    { label: 'Stress Self-Report', howToAdd: 'Complete a weekly check-in' },
    { label: 'Rumination', howToAdd: 'Complete the Thought Patterns section in check-in' },
  ],
  social: [
    { label: 'Social Connection', howToAdd: 'Complete a weekly check-in' },
    { label: 'Home Leaving', howToAdd: 'Enable location on the Arete mobile app' },
  ],
  substance: [
    { label: 'Substance Use', howToAdd: 'Complete a weekly check-in' },
  ],
};

/** Get the missing signals for a pillar given the present input labels. */
export function getMissingSignals(pillarKey: string, presentLabels: string[]): ExpectedSignal[] {
  const expected = EXPECTED_SIGNALS[pillarKey] || [];
  const present = new Set(presentLabels);
  return expected.filter(s => !present.has(s.label));
}
